import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Modal, Checkbox } from "rsuite";
import { toast } from "react-toastify";
import { submitContactUs } from "../../api";
import { CONTACT_US_MODAL } from "./constants";
import checkCircleGreen from "../../assets/img/check-circle-green.svg";
import "./contactUsModal.scss";

const initialCheckedOptions = () =>
  CONTACT_US_MODAL.CHECKBOX_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.key]: false }),
    {}
  );

const ContactUsModal = ({ open, onClose, propertyId }) => {
  const [checkedOptions, setCheckedOptions] = useState(initialCheckedOptions);
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const resetState = useCallback(() => {
    setCheckedOptions(initialCheckedOptions());
    setDescription("");
    setIsSubmitting(false);
    setIsSuccess(false);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    setCheckedOptions(initialCheckedOptions());
    setDescription("");
    setIsSubmitting(false);
  }, [onClose]);

  const handleCheckboxChange = useCallback((key) => {
    setCheckedOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    setDescription(e.target.value);
  }, []);

  const handleSubmit = useCallback(async () => {
    const selectedLabels = CONTACT_US_MODAL.CHECKBOX_OPTIONS.filter(
      (option) => checkedOptions[option.key]
    ).map((option) => option.label);

    setIsSubmitting(true);
    try {
      const res = await submitContactUs({
        propertyId,
        updatedFields: selectedLabels,
        description,
      });
      if (res?.data?.success === true) {
        setIsSuccess(true);
      } else {
        toast.error(res?.data?.message || "Failed to submit request");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to submit request");
    } finally {
      setIsSubmitting(false);
    }
  }, [checkedOptions, description, propertyId]);

  const hasSelectedOptions = Object.values(checkedOptions).some(Boolean);
  const hasDescription = description.trim().length > 0;
  const canSubmit = hasSelectedOptions && hasDescription && !isSubmitting;

  return (
    <Modal
      className={`contact-us-modal${isSuccess ? " contact-us-modal--success" : ""}`}
      open={open}
      onClose={handleClose}
      onExited={resetState}
      size="sm"
    >
      {isSuccess ? (
        <Modal.Body>
          <div className="contact-us-modal__success">
            <img
              src={checkCircleGreen}
              alt=""
              className="contact-us-modal__success-icon"
            />
            <p className="contact-us-modal__success-title">
              {CONTACT_US_MODAL.SUCCESS_TITLE}
            </p>
            <p className="contact-us-modal__success-text">
              {CONTACT_US_MODAL.SUCCESS_RESPONSE_TIME}
            </p>
            <p className="contact-us-modal__success-text">
              {CONTACT_US_MODAL.SUCCESS_APPROVAL_NOTE}
            </p>
            <button
              type="button"
              className="contact-us-modal__btn contact-us-modal__btn--cancel"
              onClick={handleClose}
            >
              {CONTACT_US_MODAL.CLOSE_BUTTON_TEXT}
            </button>
          </div>
        </Modal.Body>
      ) : (
        <>
          <Modal.Header>
            <Modal.Title>{CONTACT_US_MODAL.TITLE}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="contact-us-modal__form">
              <div className="contact-us-modal__section">
                <p className="contact-us-modal__section-title">
                  {CONTACT_US_MODAL.SECTION_TITLE}
                </p>
                <div className="contact-us-modal__checkbox-grid">
                  {CONTACT_US_MODAL.CHECKBOX_OPTIONS.map((option) => (
                    <div
                      key={option.key}
                      className="contact-us-modal__checkbox-item"
                    >
                      <Checkbox
                        checked={checkedOptions[option.key]}
                        onChange={() => handleCheckboxChange(option.key)}
                      >
                        {option.label}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </div>

              <div className="contact-us-modal__section contact-us-modal__section--description">
                <p className="contact-us-modal__section-title">
                  {CONTACT_US_MODAL.DESCRIPTION_TITLE}
                </p>
                <p className="contact-us-modal__section-subtitle">
                  {CONTACT_US_MODAL.DESCRIPTION_SUBTITLE}
                </p>
                <textarea
                  className="contact-us-modal__textarea"
                  placeholder={CONTACT_US_MODAL.TEXTAREA_PLACEHOLDER}
                  value={description}
                  onChange={handleDescriptionChange}
                />
              </div>

              <div className="contact-us-modal__actions">
                <button
                  type="button"
                  className="contact-us-modal__btn contact-us-modal__btn--cancel"
                  onClick={handleClose}
                >
                  {CONTACT_US_MODAL.CANCEL_BUTTON_TEXT}
                </button>
                <button
                  type="button"
                  className="contact-us-modal__btn contact-us-modal__btn--send"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                >
                  {CONTACT_US_MODAL.SEND_BUTTON_TEXT}
                </button>
              </div>
            </div>
          </Modal.Body>
        </>
      )}
    </Modal>
  );
};

ContactUsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ContactUsModal;
